import React, { useState, useMemo } from "react";
import { Button, Form, Input, Select, Switch, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import {
  useMoveToConfirmedMutation,
  useMoveToProcessingMutation,
  useMoveToAvailableMutation,
  useMoveToPickupMutation,
  useMoveToDeliveryMutation,
  useRejectOrderMutation,
} from "@/services/api/orders/ordersApi";
import {
  useAddDriverMutation,
  useGetDriversQuery,
} from "@/services/api/driver/driverApi";
import useAuth from "@/hooks/useAuth";

const { TextArea } = Input;
//Comment
const FulfillmentProcess = ({
  orderId,
  orderReference,
  currentStatusId,
  fulfillmentStages = [],
  onSuccess,
  onError,
}) => {
  const user = useAuth();
  const [form] = Form.useForm();
  const [driverForm] = Form.useForm();
  const [useInhouseDriver, setUseInhouseDriver] = useState(true);
  const [showDriverDialog, setShowDriverDialog] = useState(false);

  // Fetch drivers
  const { data: driversData, isLoading: driversLoading } = useGetDriversQuery();
  const [addDriver, { isLoading: addingDriver }] = useAddDriverMutation();

  // Mutations
  const [moveToConfirmed, { isLoading: movingToConfirmed }] =
    useMoveToConfirmedMutation();
  const [moveToProcessing, { isLoading: movingToProcessing }] =
    useMoveToProcessingMutation();
  const [moveToAvailable, { isLoading: movingToAvailable }] =
    useMoveToAvailableMutation();
  const [moveToPickup, { isLoading: movingToPickup }] =
    useMoveToPickupMutation();
  const [moveToDelivery, { isLoading: movingToDelivery }] =
    useMoveToDeliveryMutation();
  const [rejectOrder, { isLoading: rejecting }] = useRejectOrderMutation();

  // Get active drivers
  const activeDrivers = useMemo(() => {
    if (!driversData) return [];
    return driversData.filter((driver) => driver.status === true);
  }, [driversData]);

  // Stage mapping
  const stageMap = {
    1: "Incoming",
    2: "Confirmed",
    3: "Processing",
    4: "Available",
    5: "Pickup",
    6: "Delivery",
  };

  const nextStatusId = currentStatusId < 6 ? currentStatusId + 1 : null;
  const canReject = currentStatusId < 4;

  // Mutation map
  const getMutation = (statusId) => {
    switch (statusId) {
      case 2:
        return moveToConfirmed;
      case 3:
        return moveToProcessing;
      case 4:
        return moveToAvailable;
      case 5:
        return moveToPickup;
      case 6:
        return moveToDelivery;
      default:
        return null;
    }
  };

  const isLoading =
    movingToConfirmed ||
    movingToProcessing ||
    movingToAvailable ||
    movingToPickup ||
    movingToDelivery ||
    rejecting;

  // Transform fulfillment stages for display
  const orderStatuses = useMemo(() => {
    return fulfillmentStages.map((fulfillment, index) => ({
      id: index + 1,
      title: fulfillment.stage || "Order Stage",
      user:
        fulfillment.placed_by ||
        fulfillment.confirmed_by ||
        fulfillment.processed_by ||
        fulfillment.made_available_by ||
        fulfillment.moved_to_pickup_by ||
        fulfillment.moved_to_delivery_by ||
        "Actioned by User",
      description: fulfillment.comment || "No description available",
      timestamp:
        fulfillment.placed_at ||
        fulfillment.confirmed_at ||
        fulfillment.processed_at ||
        fulfillment.made_available_at ||
        fulfillment.moved_to_pickup_at ||
        fulfillment.moved_to_delivery_at ||
        "N/A",
      completed: true,
    }));
  }, [fulfillmentStages]);

  const handleAddDriver = async (values) => {
    try {
      await addDriver(values).unwrap();
      message.success("Driver added successfully!");
      setShowDriverDialog(false);
      driverForm.resetFields();
    } catch (error) {
      message.error("Failed to add driver. Please try again.");
    }
  };

  const handleMoveToNext = async () => {
    try {
      const values = await form.validateFields();
      const mutation = getMutation(nextStatusId);

      if (!mutation) {
        message.error("Invalid stage transition");
        return;
      }

      let payload = {
        order_ids: [orderId],
        created_by: user?.id,
        comment: values.comment || "", // Safe fallback if empty
      };

      // Special handling for pickup stage
      if (nextStatusId === 5) {
        if (useInhouseDriver) {
          payload.is_house_driver = true;
          payload.driver_id = values.driver_id;
        } else {
          payload.is_house_driver = false;
          payload.driver_name = values.driver_name;
          payload.driver_phone_no = values.driver_phone_no;
        }
      }

      await mutation(payload).unwrap();
      message.success(`Order moved to ${stageMap[nextStatusId]} successfully!`);
      form.resetFields();
      onSuccess?.();
    } catch (error) {
      if (error?.errorFields) {
        message.error("Please fill in all required fields");
      } else {
        message.error("Failed to update order status. Please try again.");
      }
      onError?.(error);
    }
  };

  const handleReject = async () => {
    try {
      const values = await form.validateFields();

      await rejectOrder({
        order_ids: [orderReference],
        created_by: user?.id,
        comment: values.comment || "",
      }).unwrap();

      message.success("Order rejected successfully!");
      form.resetFields();
      onSuccess?.();
    } catch (error) {
      message.error("Failed to reject order. Please try again.");
      onError?.(error);
    }
  };

  const renderStatusItem = (status, index, isLast) => {
    const statusClass = status.completed ? "text-green-600" : "text-gray-400";
    const dotClass = status.completed ? "bg-green-500" : "bg-gray-300";
    const borderClass = status.completed
      ? "border-green-500"
      : "border-gray-300";

    return (
      <div key={status.id} className="flex gap-4">
        <div className="flex flex-col items-center">
          <div
            className={`w-10 h-10 rounded-full bg-white border-2 ${borderClass} flex items-center justify-center flex-shrink-0`}
          >
            <div className={`w-3 h-3 rounded-full ${dotClass}`}></div>
          </div>
          {!isLast && (
            <div className="w-0.5 flex-1 bg-gray-200 mt-2 min-h-[60px]"></div>
          )}
        </div>
        <div className="flex-1 pb-4">
          <div className="flex items-center gap-2 mb-1">
            <div className={`w-2 h-2 rounded-full ${dotClass}`}></div>
            <span className={`font-semibold ${statusClass}`}>
              {status.title}
            </span>
          </div>
          {status.completed && (
            <>
              <div className="text-sm text-gray-500 mb-2">By {status.user}</div>
              <div className="text-sm text-gray-600 mb-2">
                {status.description}
              </div>
              <div className="text-xs text-gray-400">{status.timestamp}</div>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderActionForm = () => {
    if (currentStatusId === 6) {
      return (
        <div className="bg-green-50 rounded-lg p-4 text-center">
          <div className="text-green-600 font-semibold mb-1">
            Order Completed
          </div>
          <div className="text-sm text-gray-600">
            This order has been delivered
          </div>
        </div>
      );
    }

    return (
      <div className="bg-gray-50 w-[50%] rounded-lg p-4">
        <div className="flex justify-between items-center mb-3">
          <span className="font-medium text-sm">Update Order Status</span>
        </div>

        <Form form={form} layout="vertical">
          {nextStatusId === 5 && (
            <>
              <div className="mb-4 flex items-center justify-between">
                <span className="text-sm font-medium">Use In-house Driver</span>
                <Switch
                  checked={useInhouseDriver}
                  onChange={setUseInhouseDriver}
                />
              </div>

              {useInhouseDriver ? (
                <Form.Item
                  name="driver_id"
                  label="Select Driver"
                  rules={[
                    { required: true, message: "Please select a driver" },
                  ]}
                >
                  <Select
                    placeholder="Choose a driver"
                    loading={driversLoading}
                    dropdownRender={(menu) => (
                      <>
                        {menu}
                        {activeDrivers.length === 0 && (
                          <div className="text-center py-4 border-t">
                            <div className="text-gray-400 mb-2 text-sm">
                              No active drivers available
                            </div>
                            <Button
                              type="primary"
                              size="small"
                              icon={<PlusOutlined />}
                              onClick={() => setShowDriverDialog(true)}
                            >
                              Add Driver
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  >
                    {activeDrivers.map((driver) => (
                      <Select.Option key={driver.id} value={driver.id}>
                        {driver.first_name} {driver.last_name} - {driver.phone}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              ) : (
                <>
                  <Form.Item
                    name="driver_name"
                    label="Driver Name"
                    rules={[
                      { required: true, message: "Please enter driver name" },
                    ]}
                  >
                    <Input placeholder="Enter driver's full name" />
                  </Form.Item>
                  <Form.Item
                    name="driver_phone_no"
                    label="Driver Phone Number"
                    rules={[
                      { required: true, message: "Please enter phone number" },
                      {
                        pattern: /^[0-9]{11}$/,
                        message: "Enter valid 11-digit phone number",
                      },
                    ]}
                  >
                    <Input placeholder="08012345678" maxLength={11} />
                  </Form.Item>
                </>
              )}
            </>
          )}

          {/* FIX: Removed rules to make comment optional */}
          <Form.Item name="comment" label="Comment">
            <TextArea
              rows={3}
              placeholder="Add an optional comment about this status update..."
            />
          </Form.Item>

          <div className="flex items-center gap-2 pt-2">
            {canReject && (
              <Button
                onClick={handleReject}
                loading={rejecting}
                disabled={isLoading}
              >
                Reject
              </Button>
            )}
            <Button
              type="primary"
              className="bg-gradient-to-r from-[#D06D01] to-[#F9A022] !text-white !border-none ml-auto hover:opacity-90 hover:from-[#D06D01] hover:to-[#F9A022]"
              onClick={handleMoveToNext}
              loading={isLoading}
            >
              Move to {stageMap[nextStatusId]} →
            </Button>
          </div>
        </Form>
      </div>
    );
  };

  return (
    <div className="mt-6">
      <div className="mb-4">
        <span className="text-gray-500 text-xs uppercase">
          Order Status Timeline
        </span>
      </div>
      <div className="space-y-6 mb-6">
        {orderStatuses.map((status, index) =>
          renderStatusItem(status, index, index === orderStatuses.length - 1),
        )}
      </div>
      {renderActionForm()}

      {/* Add Driver Modal */}
      {showDriverDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Add New Driver</h3>
            <Form
              form={driverForm}
              layout="vertical"
              onFinish={handleAddDriver}
            >
              <Form.Item
                name="first_name"
                label="First Name"
                rules={[{ required: true, message: "Please enter first name" }]}
              >
                <Input placeholder="John" />
              </Form.Item>
              <Form.Item
                name="last_name"
                label="Last Name"
                rules={[{ required: true, message: "Please enter last name" }]}
              >
                <Input placeholder="Doe" />
              </Form.Item>
              <Form.Item
                name="phone"
                label="Phone Number"
                rules={[
                  { required: true, message: "Please enter phone number" },
                  {
                    pattern: /^\+?[0-9]{11,15}$/,
                    message: "Enter valid phone number",
                  },
                ]}
              >
                <Input placeholder="+2348012345678" />
              </Form.Item>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: "Please enter email" },
                  { type: "email", message: "Enter valid email" },
                ]}
              >
                <Input placeholder="johndoe@example.com" />
              </Form.Item>
              <Form.Item
                name="location_id"
                label="Location ID"
                rules={[
                  { required: true, message: "Please enter location ID" },
                ]}
              >
                <Input type="number" placeholder="2" />
              </Form.Item>
              <Form.Item
                name="license_number"
                label="License Number"
                rules={[
                  { required: true, message: "Please enter license number" },
                ]}
              >
                <Input placeholder="LIC-123456" />
              </Form.Item>
              <Form.Item
                name="status"
                valuePropName="checked"
                initialValue={true}
              >
                <div className="flex items-center gap-2">
                  <Switch defaultChecked />
                  <span>Active Status</span>
                </div>
              </Form.Item>
              <div className="flex gap-2 justify-end">
                <Button
                  onClick={() => {
                    setShowDriverDialog(false);
                    driverForm.resetFields();
                  }}
                >
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit" loading={addingDriver}>
                  Add Driver
                </Button>
              </div>
            </Form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FulfillmentProcess;
